import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { 
  Hammer, 
  Plus, 
  Calendar, 
  DollarSign, 
  Edit, 
  Trash2, 
  Eye,
  Filter,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  PauseCircle,
  XCircle,
  TrendingUp,
  Building2
} from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';

// Status color mapping
const statusColors = {
  'Planned': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'Completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'On Hold': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
};

const statusIcons = {
  'Planned': Clock,
  'In Progress': TrendingUp,
  'Completed': CheckCircle2,
  'On Hold': PauseCircle,
  'Cancelled': XCircle
};

const priorityColors = {
  'Low': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'Medium': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'High': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  'Urgent': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
};

export const ProjectList: React.FC = () => {
  const { projects, fetchProjects, loading, deleteProject } = useLandlordStore();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreateProject = () => {
    navigate('/landlord/projects/new');
  };

  const handleEditProject = (projectId: string) => {
    navigate(`/landlord/projects/edit/${projectId}`);
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/landlord/projects/${projectId}`);
  };

  const handleDeleteProject = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteProject(projectId);
      success('Project deleted', 'The project has been removed successfully.');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toastError('Delete failed', 'Failed to delete project. Please try again.');
    }
  };

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.property_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesType = typeFilter === 'all' || project.type === typeFilter;
    const matchesPriority = priorityFilter === 'all' || project.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesType && matchesPriority;
  });

  // Calculate summary stats
  const totalBudget = projects.reduce((sum, p) => sum + p.budget_estimated, 0);
  const totalSpent = projects.reduce((sum, p) => sum + p.budget_actual, 0);
  const activeProjects = projects.filter(p => p.status === 'In Progress').length;
  const completedProjects = projects.filter(p => p.status === 'Completed').length;

  if (loading && projects.length === 0) return <LoadingScreen />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">
            Capital Projects & Renovations
          </h1>
          <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle mt-1">
            Track structural renovations, expenditures, and contractors
          </p>
        </div>
        <button
          onClick={handleCreateProject}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus size={20} />
          New Project
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-light-card dark:bg-dark-card p-4 rounded-lg border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Projects</p>
              <p className="text-2xl font-bold text-light-text dark:text-dark-text">{projects.length}</p>
            </div>
            <Building2 className="text-primary" size={24} />
          </div>
        </div>
        
        <div className="bg-light-card dark:bg-dark-card p-4 rounded-lg border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-light-subtle dark:text-dark-subtle">Active Projects</p>
              <p className="text-2xl font-bold text-light-text dark:text-dark-text">{activeProjects}</p>
            </div>
            <TrendingUp className="text-yellow-500" size={24} />
          </div>
        </div>

        <div className="bg-light-card dark:bg-dark-card p-4 rounded-lg border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-light-subtle dark:text-dark-subtle">Completed</p>
              <p className="text-2xl font-bold text-light-text dark:text-dark-text">{completedProjects}</p>
            </div>
            <CheckCircle2 className="text-green-500" size={24} />
          </div>
        </div>

        <div className="bg-light-card dark:bg-dark-card p-4 rounded-lg border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-light-subtle dark:text-dark-subtle">Budget Utilization</p>
              <p className="text-2xl font-bold text-light-text dark:text-dark-text">
                {totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0}%
              </p>
            </div>
            <DollarSign className="text-primary" size={24} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-light-card dark:bg-dark-card p-4 rounded-lg border border-light-border dark:border-dark-border space-y-4">
        <div className="flex items-center gap-2 text-light-text dark:text-dark-text font-semibold">
          <Filter size={20} />
          <span>Filters</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Statuses</option>
            <option value="Planned">Planned</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="On Hold">On Hold</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Types</option>
            <option value="Renovation">Renovation</option>
            <option value="Repair">Repair</option>
            <option value="Construction">Construction</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Improvement">Improvement</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Priorities</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Urgent">Urgent</option>
          </select>
        </div>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="bg-light-card dark:bg-dark-card p-12 rounded-lg border border-dashed border-light-border dark:border-dark-border text-center">
          <Hammer className="mx-auto h-12 w-12 text-light-subtle dark:text-dark-subtle mb-4" />
          <h3 className="text-lg font-medium text-light-text dark:text-dark-text">No projects found</h3>
          <p className="text-light-subtle dark:text-dark-subtle mt-2">
            {projects.length === 0 
              ? "Get started by creating your first capital project." 
              : "Try adjusting your filters to find what you're looking for."}
          </p>
          {projects.length === 0 && (
            <button
              onClick={handleCreateProject}
              className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const StatusIcon = statusIcons[project.status];
            const budgetPercentage = project.budget_estimated > 0 
              ? (project.budget_actual / project.budget_estimated) * 100 
              : 0;
            const isOverBudget = budgetPercentage > 100;

            return (
              <div
                key={project.id}
                onClick={() => handleViewProject(project.id)}
                className="bg-light-card dark:bg-dark-card rounded-xl overflow-hidden shadow-sm border border-light-border dark:border-dark-border group hover:border-primary transition-colors cursor-pointer"
              >
                {/* Card Header with Status */}
                <div className="p-5 border-b border-light-border dark:border-dark-border">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-1 line-clamp-1">
                        {project.name}
                      </h3>
                      <p className="text-sm text-light-subtle dark:text-dark-subtle line-clamp-2">
                        {project.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusColors[project.status]}`}>
                        <StatusIcon size={12} />
                        {project.status}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[project.priority]}`}>
                      {project.priority}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text">
                      {project.type}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-4">
                  {/* Property Info */}
                  {project.property_name && (
                    <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle">
                      <Building2 size={16} className="mr-2" />
                      <span>{project.property_name}</span>
                    </div>
                  )}

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-light-subtle dark:text-dark-subtle">Progress</span>
                      <span className="font-medium text-light-text dark:text-dark-text">
                        {project.progress_percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${project.progress_percentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-light-subtle dark:text-dark-subtle">Budget</span>
                      <span className={`font-medium ${isOverBudget ? 'text-red-500' : 'text-light-text dark:text-dark-text'}`}>
                        KES {project.budget_actual.toLocaleString()} / KES {project.budget_estimated.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${isOverBudget ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                      />
                    </div>
                    {isOverBudget && (
                      <div className="flex items-center gap-1 text-xs text-red-500">
                        <AlertCircle size={12} />
                        <span>Over budget by {Math.round(budgetPercentage - 100)}%</span>
                      </div>
                    )}
                  </div>

                  {/* Timeline */}
                  <div className="flex items-center text-sm text-light-subtle dark:text-dark-subtle">
                    <Calendar size={16} className="mr-2" />
                    <span>
                      Started: {new Date(project.start_date).toLocaleDateString()}
                      {project.end_date && ` • Due: ${new Date(project.end_date).toLocaleDateString()}`}
                    </span>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="px-5 py-3 border-t border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProject(project.id);
                    }}
                    className="flex-1 py-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-card dark:hover:bg-dark-card transition-colors text-sm font-medium flex items-center justify-center gap-1 text-light-text dark:text-dark-text"
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewProject(project.id);
                    }}
                    className="flex-1 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <Eye size={14} />
                    View
                  </button>
                  <button
                    onClick={(e) => handleDeleteProject(project.id, e)}
                    className="px-3 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};